import { useCallback, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import { DataGrid, GridColDef, GridInitialState, GridRenderCellParams } from '@mui/x-data-grid';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import LinearProgress from '@mui/material/LinearProgress';
import Autocomplete from '@mui/material/Autocomplete';
import AddIcon from '@mui/icons-material/Add';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { useApi } from 'api/ApiProvider';
import { AiCreditApiWrapper, MonthlyAccountUsageDTO, UserCreditDTO, UserOption } from 'api/AiCreditApiWrapper';
import { getApiKey } from 'components/authentication/context/authContext';
import { ENTITY_TYPE } from 'common/constants';
import DefaultActions from 'components/DataGrid/DefaultActions';
import SearchInput from 'components/SearchInput';
import Spinner from 'components/Spinner';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function usagePercent(used: number, limit: number) {
  if (limit <= 0) return 0;
  return Math.min(100, Math.round((used / limit) * 100));
}

// ─── Edit Credit Dialog ────────────────────────────────────────────────────

interface EditDialogProps {
  row: UserCreditDTO | null;
  onClose: () => void;
  onSave: (idUser: number, limit: number) => Promise<void>;
}

function EditCreditDialog({ row, onClose, onSave }: EditDialogProps) {
  const [limit, setLimit] = useState(row?.monthlyTokenLimit ?? 0);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (row) setLimit(row.monthlyTokenLimit); }, [row]);

  const handleSave = async () => {
    if (!row) return;
    setSaving(true);
    await onSave(row.idUser, limit);
    setSaving(false);
    onClose();
  };

  return (
    <Dialog open={!!row} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Edit AI Token Credit</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {row?.fullName} ({row?.email})
        </Typography>
        <TextField
          label="Monthly Token Limit"
          type="number"
          fullWidth
          size="small"
          value={limit}
          onChange={e => setLimit(Number(e.target.value))}
          inputProps={{ min: 0 }}
          helperText="Set to 0 to disable AI access for this user."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Add Credit Dialog ────────────────────────────────────────────────────

interface AddDialogProps {
  open: boolean;
  wrapper: AiCreditApiWrapper;
  existingIds: Set<number>;
  onClose: () => void;
  onSave: (idUser: number, limit: number) => Promise<void>;
}

function AddCreditDialog({ open, wrapper, existingIds, onClose, onSave }: AddDialogProps) {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<UserOption[]>([]);
  const [selected, setSelected] = useState<UserOption | null>(null);
  const [limit, setLimit] = useState(100000);
  const [saving, setSaving] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);

  useEffect(() => {
    if (!open) { setSelected(null); setInputValue(''); setOptions([]); setLimit(100000); }
  }, [open]);

  useEffect(() => {
    if (inputValue.length < 2) { setOptions([]); return; }
    setLoadingOptions(true);
    wrapper.getUserOptions(inputValue)
      .then(opts => setOptions(opts.filter(o => !existingIds.has(o.id))))
      .finally(() => setLoadingOptions(false));
  }, [inputValue, wrapper, existingIds]);

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    await onSave(selected.id, limit);
    setSaving(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add AI Token Credit</DialogTitle>
      <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Autocomplete
          options={options}
          loading={loadingOptions}
          filterOptions={x => x}
          getOptionLabel={o => o.email}
          inputValue={inputValue}
          onInputChange={(_, v) => setInputValue(v)}
          value={selected}
          onChange={(_, v) => setSelected(v)}
          renderInput={params => (
            <TextField {...params} label="User (email)" size="small" helperText="Type at least 2 characters to search" />
          )}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          noOptionsText={inputValue.length < 2 ? 'Type to search…' : 'No users found'}
        />
        <TextField
          label="Monthly Token Limit"
          type="number"
          fullWidth
          size="small"
          value={limit}
          onChange={e => setLimit(Number(e.target.value))}
          inputProps={{ min: 0 }}
          helperText="Set to 0 to disable AI access for this user."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !selected}>
          {saving ? 'Saving…' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── User Credits Tab ─────────────────────────────────────────────────────

const INITIAL_STATE: GridInitialState = {
  pagination: { paginationModel: { pageSize: 10 } },
};

interface UserCreditsTabProps {
  wrapper: AiCreditApiWrapper;
}

function UserCreditsTab({ wrapper }: UserCreditsTabProps) {
  const [rows, setRows] = useState<UserCreditDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<UserCreditDTO | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await wrapper.getAll());
    } catch {
      setError('Failed to load user credits.');
    } finally {
      setLoading(false);
    }
  }, [wrapper]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (idUser: number, limit: number) => {
    await wrapper.upsertCredit(idUser, { monthlyTokenLimit: limit });
    await load();
  };

  const filtered = rows.filter(r =>
    r.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const existingIds = new Set(rows.map(r => r.idUser));

  const columns: GridColDef[] = [
    { field: 'fullName', headerName: 'User', flex: 1, minWidth: 160 },
    { field: 'email', headerName: 'Email', flex: 1, minWidth: 200 },
    {
      field: 'monthlyTokenLimit', headerName: 'Monthly Limit', width: 140,
      renderCell: (p: GridRenderCellParams<UserCreditDTO>) =>
        p.row.monthlyTokenLimit > 0
          ? p.row.monthlyTokenLimit.toLocaleString()
          : <Typography variant="caption" color="text.disabled">Unlimited</Typography>,
    },
    {
      field: 'usedThisMonth', headerName: 'Used This Month', width: 200,
      renderCell: (p: GridRenderCellParams<UserCreditDTO>) => (
        <Box sx={{ width: '100%', pr: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
            <Typography variant="caption">{p.row.usedThisMonth.toLocaleString()}</Typography>
            {p.row.monthlyTokenLimit > 0 && (
              <Typography variant="caption" color="text.secondary">
                {usagePercent(p.row.usedThisMonth, p.row.monthlyTokenLimit)}%
              </Typography>
            )}
          </Box>
          {p.row.monthlyTokenLimit > 0 && (
            <LinearProgress
              variant="determinate"
              value={usagePercent(p.row.usedThisMonth, p.row.monthlyTokenLimit)}
              color={usagePercent(p.row.usedThisMonth, p.row.monthlyTokenLimit) >= 90 ? 'error' : 'primary'}
              sx={{ height: 4, borderRadius: 2 }}
            />
          )}
        </Box>
      ),
    },
    {
      field: 'remaining', headerName: 'Remaining', width: 130,
      renderCell: (p: GridRenderCellParams<UserCreditDTO>) =>
        p.row.monthlyTokenLimit > 0
          ? <Typography variant="body2" color={p.row.remaining === 0 ? 'error' : 'inherit'}>{p.row.remaining.toLocaleString()}</Typography>
          : <Typography variant="caption" color="text.disabled">—</Typography>,
    },
    {
      field: 'actions', headerName: '', width: 80, sortable: false,
      renderCell: (p: GridRenderCellParams<UserCreditDTO>) => (
        <DefaultActions
          row={p.row}
          entityType={ENTITY_TYPE.ANY}
          onEdit={() => setEditing(p.row)}
          editScopes={[]}
        />
      ),
    },
  ];

  if (error) return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <SearchInput
          value={searchTerm}
          onChange={(v) => setSearchTerm(v)}
          onClear={() => setSearchTerm('')}
        />
        <Button onClick={() => setAddOpen(true)}>
          <AddIcon /> Add User
        </Button>
      </Box>
      <Box style={{ width: '100%', display: 'flex' }}>
        {loading && <Spinner />}
        <DataGrid
          rows={filtered}
          columns={columns}
          getRowId={r => r.idUser}
          loading={loading}
          initialState={INITIAL_STATE}
          pageSizeOptions={[5, 10, 20, 50]}
          disableRowSelectionOnClick
          rowHeight={52}
        />
      </Box>
      <EditCreditDialog row={editing} onClose={() => setEditing(null)} onSave={handleSave} />
      <AddCreditDialog
        open={addOpen}
        wrapper={wrapper}
        existingIds={existingIds}
        onClose={() => setAddOpen(false)}
        onSave={handleSave}
      />
    </>
  );
}

// ─── Monthly Usage Tab ────────────────────────────────────────────────────

interface MonthlyUsageTabProps {
  wrapper: AiCreditApiWrapper;
}

function MonthlyUsageTab({ wrapper }: MonthlyUsageTabProps) {
  const [data, setData] = useState<MonthlyAccountUsageDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setData(await wrapper.getMonthlyUsage(12));
      } catch {
        setError('Failed to load monthly usage.');
      } finally {
        setLoading(false);
      }
    })();
  }, [wrapper]);

  if (loading) return <Box sx={{ display: 'flex', mt: 4 }}><Spinner /></Box>;
  if (error) return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;
  if (data.length === 0) return <Typography color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>No usage data yet.</Typography>;

  const categories = data.map(d => `${MONTH_NAMES[d.month - 1]} ${d.year}`);
  const inputTokens = data.map(d => d.inputTokens);
  const outputTokens = data.map(d => d.outputTokens);
  const totalTokens = data.map(d => d.inputTokens + d.outputTokens);
  const grandTotal = totalTokens.reduce((a, b) => a + b, 0);

  const options: ApexOptions = {
    chart: { type: 'bar', toolbar: { show: false }, stacked: true },
    plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
    xaxis: { categories },
    yaxis: { labels: { formatter: (v: number) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v) } },
    legend: { position: 'top' },
    tooltip: { y: { formatter: (v: number) => v.toLocaleString() + ' tokens' } },
    colors: ['#1976d2', '#42a5f5'],
    dataLabels: { enabled: false },
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', gap: 4, mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>{grandTotal.toLocaleString()}</Typography>
          <Typography variant="caption" color="text.secondary">Total tokens (last 12 months)</Typography>
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700}>{(totalTokens.at(-1) ?? 0).toLocaleString()}</Typography>
          <Typography variant="caption" color="text.secondary">This month</Typography>
        </Box>
      </Box>
      <ReactApexChart
        type="bar"
        height={320}
        options={options}
        series={[
          { name: 'Input tokens', data: inputTokens },
          { name: 'Output tokens', data: outputTokens },
        ]}
      />
    </Box>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────

export default function AiCreditPage() {
  const api = useApi();
  const [tab, setTab] = useState(0);

  const wrapperRef = useRef<AiCreditApiWrapper | null>(null);
  if (!wrapperRef.current && api) {
    const baseUrl: string = (api.employeeApi as any).basePath ?? '';
    wrapperRef.current = new AiCreditApiWrapper(baseUrl, getApiKey);
  }

  if (!wrapperRef.current) return null;

  return (
    <Card sx={{ m: 2 }}>
      <Box sx={{ p: 3 }}>
        <Typography color="primary" variant="h2" sx={{ mb: 2 }}>
          AI Assistant — Token Management
        </Typography>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tab label="User Credits" />
          <Tab label="Monthly Usage" />
        </Tabs>
        <Box role="tabpanel" hidden={tab !== 0}>
          {tab === 0 && <UserCreditsTab wrapper={wrapperRef.current} />}
        </Box>
        <Box role="tabpanel" hidden={tab !== 1}>
          {tab === 1 && <MonthlyUsageTab wrapper={wrapperRef.current} />}
        </Box>
      </Box>
    </Card>
  );
}
